import {useModule} from '@/hooks/useModule';
import {useAction, useSelectors, useSelectorWithRefresh} from '@/store/kysely_module';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Badge, Select, Group, ActionIcon, Text, Button, Alert, Menu, Card} from '@mantine/core';
import {useSignals} from '@preact/signals-react/runtime';
import {CommentSection} from '../components/comments/comment_section';
import {IconPencil, IconStar, IconStarFilled, IconChevronLeft, IconChevronRight, IconHistory, IconInfoCircle, IconFile, IconSettings, IconArrowLeft, IconUserPlus, IconUserMinus} from '@tabler/icons-react';
import {useIsMobile, useIsWideScreen} from '@/utils/user_agent';
import {useModals} from '@/modules/music_sniper/components/modals/modals_provider';
import {RegionWithComment, WavesurferWithHook} from '@/components/wavesurfer_with_hook';
import {getRegionsForComments, getSecondsFromTimestamp, parseTimestampsWithRegions} from '@/utils/timestamp_parsing';
import {AttributesBlocksProps, AttributesBlocksBody, AttributesBlocksHeader, useAttributesBlocksState} from '@/components/attribute_editors';
import {PlayIcon} from '@/modules/audio_player/audio_player_module';
import {getVideoMimeTypeFromFileName, isAudioFile, isVideoFile, isMidiFile} from '@/utils/file_utils';
import {DBObjects} from '@/store/store_types';
import {useOptimistic} from '@/hooks/useOptimistic';
import {DashboardCard} from '@/components/new_dashboard/components/DashboardCard';
import {useNavigate} from 'react-router-dom';
import {navigateToPathForReason} from '@/modules/navigation/navigation_module';
import {useCurrentObjects} from '@/store/modules/workspaces_module';
import {AudioSeekBar} from '@/components/AudioSeekBar';
import {MidiViewer} from '../components/midi_viewer/midi_viewer';
import {notifications} from '@mantine/notifications';
import {LoadingIndicator} from '@/components/common/LoadingIndicator';

type Props = {
    file_id: string;
    workspace_id: string;
    revisionNum?: number;
}

export const FileViewPage = (props: Props) => {
    useSignals();

    const selectors = useSelectors();
    const navigate = useNavigate();
    const currentObject = useCurrentObjects();
    const [videoFileTimestamp, setVideoFileTimestamp] = useState<{timestamp: string} | null>(null);

    const [baseFileInfo, refreshFileInfo, , error] = useSelectorWithRefresh(
        'getFileInfo',
        props.revisionNum ? {file_id: props.file_id, revision_num: props.revisionNum} : {file_id: props.file_id},
        null,
        {workspace_id: props.workspace_id}
    );

    const [fileInfo, setFileInfo] = useOptimistic(baseFileInfo);

    // TODO: Fetch all revisions for navigation when getFileRevisions selector is implemented
    // const { data: allRevisions } = useQuery({
    //     queryKey: ['fileRevisions', props.file_id],
    //     queryFn: async () => {
    //         return await selectors.getFileRevisions({ file_id: props.file_id }, { workspace_id: props.workspace_id });
    //     },
    //     enabled: Boolean(fileInfo),
    // });
    const allRevisions = undefined as any[] | undefined;

    // TODO: Fetch comment counts for each revision when FileRevision parent_type is supported
    // const { data: revisionCommentCounts } = useQuery({
    //     queryKey: ['revisionCommentCounts', props.file_id],
    //     queryFn: async () => {
    //         if (!allRevisions || allRevisions.length === 0) return [];
    //         const counts = await Promise.all(
    //             allRevisions.map(async (revision: any) => {
    //                 const comments = await selectors.getCommentsAndRepliesForParent(
    //                     {
    //                         parent_id: revision.id,
    //                         parent_type: 'File'
    //                     },
    //                     { workspace_id: props.workspace_id }
    //                 );
    //                 return {
    //                     revision_num: revision.revision_num,
    //                     comment_count: comments.length
    //                 };
    //             })
    //         );
    //         return counts;
    //     },
    //     enabled: Boolean(allRevisions),
    // });

    const audioPlayerModule = useModule('AudioPlayer');
    const localFilesModule = useModule('LocalDesktopFiles');

    const isMobile = useIsMobile();
    const [newTimestamp, setNewTimestamp] = useState<{timestamp: string} | null>(null);
    // const [regionsEnabled, setRegionsEnabled] = useState(!isMobile);

    useEffect(() => {
        if (newTimestamp && isMobile) {
            const commentSidebar = document.querySelector('.comment-sidebar');
            if (commentSidebar) {
                commentSidebar.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [newTimestamp, isMobile]);

    const handleClickedTimestamp = async (timestamp: string) => {
        if (fileInfo && isAudioFile(fileInfo.name)) {
            if (!isCurrentFile) {
                const audio = (window as unknown as {audioElement?: HTMLAudioElement}).audioElement;
                if (audio) {
                    audio.pause();
                }

                audioPlayerModule.playFile(fileInfo);
                await new Promise(r => setTimeout(r, 1000));
            }

            const seconds = getSecondsFromTimestamp(timestamp);
            audioPlayerModule.seekToSeconds(seconds);
        }

        if (fileInfo && isVideoFile(fileInfo.name)) {
            setVideoFileTimestamp({timestamp});
        }
    };

    const attributesBlocksProps: AttributesBlocksProps = {
        values: fileInfo?.attributes,
        parent_id: props.file_id,
        parent_type: 'File',
        workspace_id: props.workspace_id,
        refresh: refreshFileInfo,
        clickedTimestamp: handleClickedTimestamp,
    };

    const attributesBlocksHookState = useAttributesBlocksState(attributesBlocksProps);

    const [comments, refreshComments] = useSelectorWithRefresh(
        'getCommentsAndRepliesForParent',
        {parent_id: props.file_id, parent_type: 'File'},
        [],
        {workspace_id: props.workspace_id}
    );

    const formatTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    const handleTrimAction = async (startTime: number, endTime: number) => {
        const defaultFileName = `${fileInfo?.name?.split('.')[0] || ''} - trimmed ${formatTime(startTime)}-${formatTime(endTime)}`;

        let suffix = '';
        const lastPeriodIndex = fileInfo?.name.lastIndexOf('.');
        if (lastPeriodIndex !== undefined && lastPeriodIndex !== -1) {
            suffix = fileInfo!.name.substring(lastPeriodIndex);
        }

        modals.openModal('TextInputModal', {
            title: 'Name trimmed file',
            inputName: 'File name',
            initialValue: defaultFileName,
            suffix,
            onSubmit: async (fileName) => {
                let notificationId: string | undefined;
                let pollInterval: ReturnType<typeof setInterval> | undefined;
                let isHandled = false;

                try {
                    const fullName = `${fileName}${suffix}`;

                    const result = await actions.trimAudioFile({
                        file_id: props.file_id,
                        start_time: startTime,
                        end_time: endTime,
                        new_file_name: fullName,
                    }, {workspace_id: props.workspace_id});

                    if (result.status === 'processing' && result.jobId) {
                        notificationId = notifications.show({
                            id: result.jobId,
                            title: 'Processing Audio Trim',
                            message: `Creating "${fullName}"`,
                            color: 'blue',
                            loading: true,
                            autoClose: false,
                        });

                        pollInterval = setInterval(async () => {
                            try {
                                if (isHandled) {
                                    return;
                                }

                                const status = await selectors.getTrimJobStatus({
                                    job_id: result.jobId!,
                                    workspace_id: props.workspace_id,
                                }, {workspace_id: props.workspace_id});

                                if (isHandled) {
                                    return;
                                }

                                if (status.status === 'completed') {
                                    isHandled = true;

                                    if (pollInterval) {
                                        clearInterval(pollInterval);
                                    }

                                    notifications.hide(result.jobId!);

                                    let messageText = 'Audio file trimmed successfully. Click to view.';

                                    if (status.newFileId) {
                                        const newFileInfo = await selectors.getFileInfo({
                                            file_id: status.newFileId,
                                        }, {workspace_id: props.workspace_id});

                                        if (newFileInfo) {
                                            const durationStr = newFileInfo.attributes?.duration;
                                            let formattedDuration = '';

                                            if (durationStr) {
                                                const totalSeconds = parseFloat(durationStr);
                                                formattedDuration = ` (${formatTime(totalSeconds)})`;
                                            }

                                            messageText = `Created "${newFileInfo.name}"${formattedDuration}. Click to view.`;
                                        }
                                    }

                                    const completionNotificationId = notifications.show({
                                        title: 'Audio Trimmed',
                                        message: messageText,
                                        color: 'green',
                                        autoClose: 5000,
                                        onClick: () => {
                                            if (status.newFileId) {
                                                navigateToPathForReason(
                                                    navigate,
                                                    'navigate_to_trimmed_file',
                                                    `/workspaces/${props.workspace_id}/files/${status.newFileId}`
                                                );
                                                notifications.hide(completionNotificationId);
                                            }
                                        },
                                        style: { cursor: status.newFileId ? 'pointer' : 'default' },
                                    });

                                    refreshFileInfo();
                                } else if (status.status === 'failed') {
                                    isHandled = true;

                                    if (pollInterval) {
                                        clearInterval(pollInterval);
                                    }

                                    notifications.hide(result.jobId!);
                                    notifications.show({
                                        title: 'Trim Failed',
                                        message: status.error || 'Failed to trim audio file',
                                        color: 'red',
                                    });
                                }
                            } catch (error) {
                                console.error('Error polling job status:', error);
                            }
                        }, 2000);
                    } else if (result.status === 'completed') {
                        refreshFileInfo();
                    }
                } catch (error: unknown) {
                    if (pollInterval) {
                        clearInterval(pollInterval);
                    }
                    if (notificationId) {
                        notifications.hide(notificationId);
                    }

                    notifications.show({
                        title: 'Trim Failed',
                        message: error instanceof Error ? error.message : 'Failed to trim audio file',
                        color: 'red',
                    });
                }
            },
        });
    };

    const regions: RegionWithComment[] = useMemo(() => {
        return [
            ...getRegionsForComments(comments),
            ...parseTimestampsWithRegions(fileInfo?.attributes?.chord_lyrics || '').map(t => ({
                parsedTimestamp: t,
                comment: undefined,
            })),
        ]
    }, [comments, fileInfo?.attributes.chord_lyrics, fileInfo?.attributes.description]);

    useEffect(() => {
        if (fileInfo) {
            // audioPlayerModule.playFile(fileInfo);
        }
    }, [fileInfo]);

    const playbackStatus = audioPlayerModule.useAudioPlaybackStatus();

    const globalKeyListenerModule = useModule('GlobalKeyListener');

    // Expose current file to global key listeners
    useEffect(() => {
        globalKeyListenerModule.setCurrentFileInfo(fileInfo);

        return () => {
            globalKeyListenerModule.setCurrentFileInfo(null);
        };
    }, [fileInfo, globalKeyListenerModule]);

    const isWideScreen = useIsWideScreen();

    const modals = useModals();

    const actions = {
        updateFileName: useAction('updateFileName'),
        deleteFile: useAction('deleteFile'),
        starObject: useAction('starObject'),
        unstarObject: useAction('unstarObject'),
        trimAudioFile: useAction('trimAudioFile'),
        publishTrackToProfile: useAction('publishTrackToProfile'),
        unpublishTrackFromProfile: useAction('unpublishTrackFromProfile'),
        getFileInfoWithRaw: selectors.getFileInfoWithRaw,
    };

    // Navigation helpers
    const currentRevisionNum = props.revisionNum || (allRevisions ? allRevisions[0]?.revision_num : 1);
    const currentRevisionIndex = allRevisions ? allRevisions.findIndex((r: any) => r.revision_num === currentRevisionNum) : 0;
    const canGoToPreviousRevision = allRevisions && currentRevisionIndex < allRevisions.length - 1;
    const canGoToNextRevision = allRevisions && currentRevisionIndex > 0;

    const navigateToRevision = (revisionNum: number | null) => {
        if (revisionNum === null || (allRevisions && revisionNum === allRevisions[0]?.revision_num)) {
            // Navigate to current version
            navigate(`/workspaces/${props.workspace_id}/files/${props.file_id}`);
        } else {
            // Navigate to specific revision
            navigate(`/workspaces/${props.workspace_id}/files/${props.file_id}/revisions/${revisionNum}`);
        }
    };

    const openRevisionHistory = () => {
        // TODO: Implement RevisionHistoryModal
        console.log('Revision history clicked - modal not yet implemented');
        // modals.openModal('RevisionHistoryModal', {
        //     entityType: 'file',
        //     entityId: props.file_id,
        //     workspaceId: props.workspace_id,
        //     entityName: fileInfo?.name,
        // });
    };

    const [waveformAndDuration] = useSelectorWithRefresh('getFileWaveformAndDuration', {file_id: fileInfo?.id}, null, {workspace_id: fileInfo?.workspace_id || ''})

    const trimMetadata = useMemo(() => {
        try {
            return fileInfo?.attributes?.trim_metadata ? JSON.parse(fileInfo.attributes.trim_metadata) : null;
        } catch {
            return null;
        }
    }, [fileInfo?.attributes?.trim_metadata]);

    const trimSourceFileId = trimMetadata?.source_file_id;
    const trimSourceProjectId = trimMetadata?.source_project_id;
    const trimStartTime = trimMetadata?.start_time?.toString();
    const trimEndTime = trimMetadata?.end_time?.toString();

    const [trimSourceFile] = useSelectorWithRefresh('getFileInfo', {file_id: trimSourceFileId || ''}, null, {workspace_id: props.workspace_id, ignore: !trimSourceFileId});
    const [trimSourceProject] = useSelectorWithRefresh('getProject', {project_id: trimSourceProjectId || '', includeHidden: false}, null, {workspace_id: props.workspace_id, ignore: !trimSourceProjectId});

    if (error) {
        return (
            'Error fetching file'
        );
    }

    if (!fileInfo) {
        return <LoadingIndicator />;
    }

    const isCurrentFile = playbackStatus?.fileInfo.id === fileInfo.id;
    const isPlaying = isCurrentFile && playbackStatus?.isPlaying;

    const fileDetailsHeader = (
        <AttributesBlocksHeader
            {...attributesBlocksProps}
            hookState={attributesBlocksHookState}
            dotMenuItems={[
                <Menu.Item
                    key='edit-file-name'
                    leftSection={<IconPencil size={14} />}
                    onClick={() => {
                        let fileName = fileInfo.name;
                        let suffix = '';
                        const lastPeriodIndex = fileInfo.name.lastIndexOf('.');
                        if (lastPeriodIndex !== -1) {
                            fileName = fileInfo.name.substring(0, lastPeriodIndex);
                            suffix = fileInfo.name.substring(lastPeriodIndex);
                        }

                        modals.openModal('TextInputModal', {
                            title: 'Edit file',
                            inputName: 'File name',
                            initialValue: fileName,
                            suffix,
                            onSubmit: async (fileName) => {
                                const fullName = `${fileName}${suffix}`;
                                setFileInfo(prev => {
                                    if (!prev) {
                                        return prev;
                                    }
                                    return {
                                        ...prev,
                                        name: fullName,
                                    };
                                });

                                await actions.updateFileName({file_id: fileInfo.id, new_name: fullName}, {workspace_id: fileInfo.workspace_id});
                                refreshFileInfo();
                            },
                        });
                    }}
                >
                    Rename File
                </Menu.Item>,
                fileInfo.isStarred ? (
                    <Menu.Item
                        key='unstar-file'
                        leftSection={<IconStarFilled size={14} color='gold' />}
                        onClick={async () => {
                            modals.openModal('ConfirmationModal', {
                                title: 'Remove star from file?',
                                message: 'The file will no longer appear in your starred files.',
                                confirmLabel: 'Remove',
                                onConfirm: async () => {
                                    await actions.unstarObject({parent_id: fileInfo.id, parent_type: 'File'}, {workspace_id: fileInfo.workspace_id});
                                    refreshFileInfo();
                                },
                            });
                        }}
                    >
                        Unstar File
                    </Menu.Item>
                ) : (
                    <Menu.Item
                        key='star-file'
                        leftSection={<IconStar size={14} />}
                        onClick={async () => {
                            await actions.starObject({parent_id: fileInfo.id, parent_type: 'File'}, {workspace_id: fileInfo.workspace_id});
                            refreshFileInfo();
                        }}
                    >
                        Star File
                    </Menu.Item>
                ),
                fileInfo.file_revision_id && (
                    fileInfo.isPublishedToProfile ? (
                        <Menu.Item
                            key='unpublish-from-profile'
                            leftSection={<IconUserMinus size={14} />}
                            onClick={async () => {
                                await actions.unpublishTrackFromProfile({file_revision_id: fileInfo.file_revision_id!}, {workspace_id: fileInfo.workspace_id});
                                refreshFileInfo();
                            }}
                        >
                            Remove from Profile
                        </Menu.Item>
                    ) : (
                        <Menu.Item
                            key='publish-to-profile'
                            leftSection={<IconUserPlus size={14} />}
                            onClick={async () => {
                                await actions.publishTrackToProfile({file_revision_id: fileInfo.file_revision_id!}, {workspace_id: fileInfo.workspace_id});
                                refreshFileInfo();
                            }}
                        >
                            Publish to Profile
                        </Menu.Item>
                    )
                ),
            ]}
        />
    );

const headerContent = (
        <div
            className='file-header-section'
            style={!isWideScreen ? {
                position: 'sticky',
                top: 0,
                zIndex: 100,
                backgroundColor: 'var(--mantine-color-body)',
            } : undefined}
        >
            <Group gap="xs" wrap="nowrap" style={{overflow: 'hidden', padding: '10px'}}>
                {!isWideScreen && (
                    <ActionIcon
                        variant="subtle"
                        size="md"
                        onClick={() => {
                            if (currentObject.project) {
                                navigateToPathForReason(
                                    navigate,
                                    'navigate_to_project_from_file_header',
                                    `/workspaces/${currentObject.project.workspace_id}/projects/${currentObject.project.id}`
                                );
                            }
                        }}
                    >
                        <IconArrowLeft size={18} />
                    </ActionIcon>
                )}
                {isAudioFile(fileInfo.name) && <PlayIcon fileInfo={fileInfo} size={20} />}
                <Text
                    size={isWideScreen ? "lg" : "sm"}
                    fw={500}
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                    }}
                >
                    {fileInfo.name.length > 50 ? fileInfo.name.substring(0, 50) + '...' : fileInfo.name}
                    {props.revisionNum && (
                        <Badge ml="sm" color="blue" variant="light" size="sm">
                            Revision {props.revisionNum}
                        </Badge>
                    )}
                </Text>
                {localFilesModule && (
                    <div style={{marginLeft: 'auto'}}>
                        <localFilesModule.openWithFileHelpers.OpenWithComponent
                            fileId={fileInfo.id}
                            relativePath={fileInfo.relative_path}
                            workspaceId={fileInfo.workspace_id}
                        />
                    </div>
                )}
            </Group>

            {allRevisions && allRevisions.length > 1 && isWideScreen && (
                <Group gap="xs" style={{padding: '0 10px 10px 10px'}} justify="flex-end">
                    <ActionIcon
                        variant="light"
                        size="sm"
                        disabled={!canGoToPreviousRevision}
                        onClick={() => {
                            if (canGoToPreviousRevision && allRevisions) {
                                navigateToRevision(allRevisions[currentRevisionIndex + 1].revision_num);
                            }
                        }}
                        title="Previous revision (older)"
                    >
                        <IconChevronLeft size={14} />
                    </ActionIcon>

                    <Select
                        size="xs"
                        value={currentRevisionNum.toString()}
                        onChange={(value) => {
                            if (value) {
                                const revisionNum = parseInt(value, 10);
                                navigateToRevision(revisionNum);
                            }
                        }}
                        data={allRevisions.map((revision: any) => ({
                            value: revision.revision_num.toString(),
                            label: revision.revision_name
                        }))}
                        style={{ minWidth: '150px' }}
                    />

                    <ActionIcon
                        variant="light"
                        size="sm"
                        disabled={!canGoToNextRevision}
                        onClick={() => {
                            if (canGoToNextRevision && allRevisions) {
                                navigateToRevision(allRevisions[currentRevisionIndex - 1].revision_num);
                            }
                        }}
                        title="Next revision (newer)"
                    >
                        <IconChevronRight size={14} />
                    </ActionIcon>

                    <ActionIcon
                        variant="light"
                        size="sm"
                        onClick={openRevisionHistory}
                        title="View revision history"
                    >
                        <IconHistory size={14} />
                    </ActionIcon>
                </Group>
            )}

            {props.revisionNum && allRevisions && (
                <Alert
                    icon={<IconInfoCircle size={16} />}
                    color="blue"
                    variant="light"
                    style={{ margin: '0 10px 10px 10px' }}
                >
                    <Group justify="space-between" align="center">
                        <Text size="sm">
                            You're viewing revision {props.revisionNum} of this file.
                        </Text>
                        <Button
                            size="xs"
                            variant="light"
                            onClick={() => navigateToRevision(null)}
                        >
                            View Current Version
                        </Button>
                    </Group>
                </Alert>
            )}
        </div>
    );

    return (
        <div className={isWideScreen ? 'file-view-page-desktop' : 'file-view-page-mobile'}>
            {!isWideScreen && headerContent}

            {!isWideScreen && isAudioFile(fileInfo.name) && waveformAndDuration?.duration && waveformAndDuration?.waveform_data && (
                <div style={{
                    position: 'sticky',
                    top: '46px',
                    zIndex: 99,
                    backgroundColor: 'var(--mantine-color-body)',
                    paddingLeft: '10px',
                    paddingRight: '10px',
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    marginBottom: '1rem',
                    marginTop: '-10px',
                }}>
                    <Card shadow="sm" padding="md" radius="md" withBorder>
                        <WavesurferWithHook
                            height={100}
                            isCurrentFile={isCurrentFile}
                            isPlaying={isCurrentFile && isPlaying}
                            onTimestampAction={(timestamp, action) => {
                                if (action === 'comment') {
                                    setNewTimestamp({timestamp});
                                }
                            }}
                            onTrimAction={handleTrimAction}
                            // regionsEnabled={regionsEnabled}
                            // onRegionsEnabledChange={(enabled) => {
                            //     setRegionsEnabled(enabled);
                            // }}
                            duration={waveformAndDuration.duration}
                            peaks={waveformAndDuration.waveform_data}
                            regions={regions}
                            onSeek={seconds => {
                                audioPlayerModule.playAndSeek(fileInfo, seconds);
                            }}
                        />
                        <div style={{marginTop: '1rem'}}>
                            <AudioSeekBar fileInfo={fileInfo} showTimeLabels={false} />
                        </div>
                    </Card>
                </div>
            )}

            <div className='main-content-wrapper'>
                <div className='file-detail'>
                    {isWideScreen && headerContent}
                    {isMidiFile(fileInfo.name) && (
                        <div style={{marginBottom: '20px'}}>
                            <MidiViewer fileInfo={fileInfo} />
                        </div>
                    )}
                    {isVideoFile(fileInfo.name) && (
                        <div>
                            <VideoPlayer
                                fileInfo={fileInfo}
                                timestamp={videoFileTimestamp}
                            />
                        </div>
                    )}
                    {isWideScreen && isAudioFile(fileInfo.name) && waveformAndDuration?.duration && waveformAndDuration?.waveform_data && (
                        <div style={{
                            marginBottom: '1rem',
                            marginRight: '1rem',
                        }}>
                            <Card shadow="sm" padding="md" radius="md" withBorder>
                                <WavesurferWithHook
                                    height={100}
                                    isCurrentFile={isCurrentFile}
                                    isPlaying={isCurrentFile && isPlaying}
                                    onTimestampAction={(timestamp, action) => {
                                        if (action === 'comment') {
                                            setNewTimestamp({timestamp});
                                        }
                                    }}
                                    onTrimAction={handleTrimAction}
                                    // regionsEnabled={regionsEnabled}
                                    // onRegionsEnabledChange={(enabled) => {
                                    //     setRegionsEnabled(enabled);
                                    // }}
                                    duration={waveformAndDuration.duration}
                                    peaks={waveformAndDuration.waveform_data}
                                    regions={regions}
                                    onSeek={seconds => {
                                        audioPlayerModule.playAndSeek(fileInfo, seconds);
                                    }}
                                />
                            </Card>
                        </div>
                    )}

                    {fileInfo?.attributes && (
                        <div style={{
                            maxWidth: isWideScreen ? '600px' : '900px',
                            marginTop: '1rem',
                        }}>
                            <DashboardCard
                                title={(
                                    <>
                                        File Details
                                        <Group
                                            display='inline-block'
                                            ml="md"
                                        >
                                            {fileDetailsHeader}
                                        </Group>
                                    </>
                                )}
                                icon={<IconSettings size={18} />}
                            >
                                <Group gap="md" mb="md">
                                    {waveformAndDuration?.duration && (
                                        <Group gap="xs">
                                            <IconFile size={18} />
                                            <Text size="sm">Duration: {Math.floor(waveformAndDuration.duration / 60)}:{Math.floor(waveformAndDuration.duration % 60).toString().padStart(2, '0')}</Text>
                                        </Group>
                                    )}
                                </Group>

                                {trimSourceFileId && (trimStartTime || trimEndTime) && (
                                    <Card withBorder p="sm" mb="md" style={{backgroundColor: 'var(--mantine-color-blue-light)'}}>
                                        <Text size="sm" fw={600} mb="xs">Trimmed from:</Text>
                                        {trimSourceFile && (
                                            <Text size="sm" mb="xs">
                                                File: <a
                                                    href={`/workspaces/${props.workspace_id}/files/${trimSourceFileId}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        navigateToPathForReason(
                                                            navigate,
                                                            'navigate_from_spa_navigate_call',
                                                            `/workspaces/${props.workspace_id}/files/${trimSourceFileId}`
                                                        );
                                                    }}
                                                    style={{color: 'var(--mantine-color-blue-filled)', textDecoration: 'underline'}}
                                                >
                                                    {trimSourceFile.name}
                                                </a>
                                            </Text>
                                        )}
                                        {trimSourceProject && (
                                            <Text size="sm" mb="xs">
                                                Project: <a
                                                    href={`/workspaces/${props.workspace_id}/projects/${trimSourceProjectId}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        navigateToPathForReason(
                                                            navigate,
                                                            'navigate_from_spa_navigate_call',
                                                            `/workspaces/${props.workspace_id}/projects/${trimSourceProjectId}`
                                                        );
                                                    }}
                                                    style={{color: 'var(--mantine-color-blue-filled)', textDecoration: 'underline'}}
                                                >
                                                    {trimSourceProject.name}
                                                </a>
                                            </Text>
                                        )}
                                        {trimStartTime && trimEndTime && (
                                            <Text size="sm">
                                                Time range: {Math.floor(parseFloat(trimStartTime) / 60)}:{Math.floor(parseFloat(trimStartTime) % 60).toString().padStart(2, '0')} - {Math.floor(parseFloat(trimEndTime) / 60)}:{Math.floor(parseFloat(trimEndTime) % 60).toString().padStart(2, '0')}
                                            </Text>
                                        )}
                                    </Card>
                                )}

                                <AttributesBlocksBody
                                    {...attributesBlocksProps}
                                    hookState={attributesBlocksHookState}
                                />
                            </DashboardCard>
                        </div>
                    )}
                </div>

                <div className='comment-sidebar'>
                <CommentSection
                    newTimestamp={newTimestamp}
                    parent_id={props.file_id}
                    parent_type='File'
                    refreshComments={refreshComments}
                    clickedTimestamp={handleClickedTimestamp}
                    onInitialFocus={() => {
                        if (!isCurrentFile) {
                            return '';
                        }

                        const audio = (window as unknown as {audioElement?: HTMLAudioElement}).audioElement;
                        if (!audio) {
                            return '';
                        }

                        const currentTime = audio.currentTime;
                        const minutes = Math.floor(currentTime / 60);
                        const seconds = Math.floor(currentTime % 60);

                        const timestamp = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
                        return `${timestamp} `;
                    }}
                />
                </div>
            </div>
        </div>
    );
};

const VideoPlayer = ({fileInfo, timestamp}: {fileInfo: DBObjects['File']; timestamp: {timestamp: string} | null}) => {
    const fileClient = useModule('MSFiles').client();
    const [src, setSrc] = useState('');
    const isMobile = useIsMobile();

    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        fileClient.getFileSrc(fileInfo).then(setSrc);
    }, [fileInfo]);

    useEffect(() => {
        if (!timestamp) {
            return;
        }

        const seconds = getSecondsFromTimestamp(timestamp.timestamp);
        const video = videoRef.current;
        if (!video) {
            return;
        }

        video.currentTime = seconds;
    }, [timestamp]);

    return (
        <div>
            {src && (
                <video
                    ref={videoRef}
                    controls
                    autoPlay={!isMobile}
                    playsInline
                    style={{maxWidth: '100%', width: '800px'}}
                >
                    <source
                        src={src}
                        type={getVideoMimeTypeFromFileName(fileInfo.name)}
                    />
                </video>
            )}
        </div>
    );
};
